import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './ProofSection.css';

function parseStatValue(str) {
  const match = str.match(/^(.*?)(\d[\d,.]*)(.*?)$/);
  if (!match) return null;
  const pre = match[1];
  const numStr = match[2];
  const post = match[3];
  const hasDotThousands = /\.\d{3}/.test(numStr) && !/\.\d{1,2}$/.test(numStr);
  const hasCommaThousands = /,\d{3}/.test(numStr);
  const hasCommaDecimal = /,\d{1,2}$/.test(numStr) && !hasCommaThousands;
  let target, fmt;
  if (hasCommaThousands) {
    target = parseFloat(numStr.replace(/,/g, ''));
    fmt = n => Math.round(n).toLocaleString('en-US');
  } else if (hasDotThousands) {
    target = parseFloat(numStr.replace(/\./g, ''));
    fmt = n => Math.round(n).toLocaleString('de-DE');
  } else if (hasCommaDecimal) {
    target = parseFloat(numStr.replace(',', '.'));
    const d = numStr.split(',')[1].length;
    fmt = n => n.toFixed(d).replace('.', ',');
  } else {
    target = parseFloat(numStr);
    const parts = numStr.split('.');
    const d = parts[1] ? parts[1].length : 0;
    fmt = n => d > 0 ? n.toFixed(d) : String(Math.round(n));
  }
  return { pre, target, post, fmt };
}

const AnimatedValue = ({ value, visible }) => {
  const [display, setDisplay] = useState(value);
  const hasRun = useRef(false);
  const parsed = useRef(parseStatValue(value));

  const animate = useCallback(() => {
    const p = parsed.current;
    if (!p) return;
    const duration = 2000;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(`${p.pre}${p.fmt(eased * p.target)}${p.post}`);
      if (t < 1) requestAnimationFrame(tick);
    };
    setDisplay(`${p.pre}${p.fmt(0)}${p.post}`);
    requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (!visible || hasRun.current) return;
    hasRun.current = true;
    animate();
  }, [visible, animate]);

  return <>{display}</>;
};

const ProofSection = () => {
  const { language } = useLanguage();
  const t = translations[language].proofSection;
  const [labelRef, labelVisible] = useScrollAnimation({ threshold: 0.2 });
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.1 });
  const [bodyRef, bodyVisible] = useScrollAnimation({ threshold: 0.2 });
  const [boldRef, boldVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="proof-section">
      <div className="proof-section-inner">
        <span
          ref={labelRef}
          className={`section-label scroll-fade-in ${labelVisible ? 'is-visible' : ''}`}
        >
          {t.label}
        </span>
        <h2
          ref={titleRef}
          className={`proof-section-title scroll-fade-in scroll-animation-delay-1 ${titleVisible ? 'is-visible' : ''}`}
        >
          {(t.titleLines || [t.title]).map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
        <p className="proof-section-subtitle">{t.subtitle}</p>

        <div
          ref={statsRef}
          className="proof-section-stats"
        >
          {t.stats.map((stat, i) => (
            <div
              key={i}
              className={`proof-section-stat-card scroll-fade-in-stagger ${statsVisible ? 'is-visible' : ''}`}
            >
              <span className={`proof-section-stat-value${i === 1 ? ' proof-section-stat-value--silver' : ''}`}>{stat.prefix && <span className="proof-section-stat-prefix">{stat.prefix} </span>}<AnimatedValue value={stat.value} visible={statsVisible} />{stat.unit && <span className="proof-section-stat-unit">{stat.unit}</span>}</span>
              <span className="proof-section-stat-desc">{stat.description}</span>
            </div>
          ))}
        </div>

        <p className="proof-section-disclaimer">{t.disclaimer}</p>

        <div className="proof-section-divider" />

        <p
          ref={bodyRef}
          className={`proof-section-body scroll-fade-in ${bodyVisible ? 'is-visible' : ''}`}
        >
          {t.body}
        </p>

        <p
          ref={boldRef}
          className={`proof-section-bold scroll-fade-in ${boldVisible ? 'is-visible' : ''}`}
        >
          {t.bold} <span className="proof-section-bold-highlight">{t.boldHighlight}</span>
        </p>
      </div>
    </section>
  );
};

export default ProofSection;
